import { ReviewedDetectionDto } from "../../services/ocr/InspectionReviewService";
import { stableKeyFor } from "./detectionAssessment";

/**
 * Which parts of an approved observation came from a human rather than the machine.
 *
 * A reader of the approved list needs to be able to tell a corrected main code from an extracted
 * one — otherwise the reviewed record reads as though the AI produced all of it, and the
 * reviewer's work is invisible in the very document that exists to carry it.
 *
 * Derived by comparing each approved row against the machine row it came from, rather than stored
 * on the row. Nothing to migrate, nothing that can go stale, and it stays correct when a reviewer
 * edits a value back to what OCR originally read — a stored flag would go on claiming a change
 * that is no longer there.
 */

/** The coded observation fields. Deliberately excludes the reviewer-only ones — see below. */
export const OCR_CODED_FIELDS = [
  "mainCode",
  "c1",
  "c2",
  "q1",
  "q2",
  "sa",
  "positionFrom",
  "positionTo",
  "distance",
] as const;

export type OcrCodedField = typeof OCR_CODED_FIELDS[number];

/**
 * The coded fields under one set of names.
 *
 * Two row shapes carry these — the review DTO (`mainCode`, `positionFrom`) and the display row
 * the lists render (`main_Code`, `postionFrom`, note the long-standing typo) — so callers convert
 * first and the comparison itself only ever sees one shape. One rule, stated once.
 */
export type CodedFields = Partial<Record<OcrCodedField, unknown>> & {
  /**
   * The row's video position, carried for IDENTITY only — never compared.
   *
   * Two rows can share a station and a main code (see stableKeyFor), so without this the diff
   * would pair an approved row with whichever duplicate happened to be indexed last and report
   * the difference between two unrelated observations as reviewer edits.
   */
  timeSeconds?: number;
};

export const codedFromReviewDto = (d: ReviewedDetectionDto): CodedFields => ({
  timeSeconds: d.timeSeconds,
  mainCode: d.mainCode,
  c1: d.c1,
  c2: d.c2,
  q1: d.q1,
  q2: d.q2,
  sa: d.sa,
  positionFrom: d.positionFrom,
  positionTo: d.positionTo,
  distance: d.distance,
});

export const codedFromDisplayRow = (d: any): CodedFields => ({
  mainCode: d?.main_Code,
  c1: d?.c1,
  c2: d?.c2,
  q1: d?.q1,
  q2: d?.q2,
  sa: d?.sa,
  positionFrom: d?.postionFrom,
  positionTo: d?.positionTo,
  distance: d?.distance,
});

const normalise = (value: unknown): string =>
  value === undefined || value === null ? "" : String(value).trim();

/** Distance is a number on one shape and a formatted string on the other. */
const sameValue = (field: OcrCodedField, a: unknown, b: unknown): boolean => {
  if (field === "distance") {
    const na = Number(a ?? 0);
    const nb = Number(b ?? 0);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na.toFixed(2) === nb.toFixed(2);
  }
  return normalise(a) === normalise(b);
};

/**
 * The coded fields a human changed, or every field when there is no machine counterpart at all
 * (the reviewer added the row outright).
 *
 * Condition, risk and the reviewer comment are NOT reported. Those are the reviewer's own entry
 * by definition — the machine has no opinion on them — so marking them would put a marker on
 * every assessed row and thereby say nothing.
 *
 * Caveat worth knowing: the stable key is distance + main code, so a row whose distance or main
 * code was itself the edit no longer matches its machine row and comes back as added rather than
 * edited. The row's persisted `decision` (Edited vs Added) stays correct in that case, which is
 * why the row-level badge reads from that and only the per-field markers come from here.
 */
export function changedCodedFields(
  approved: CodedFields,
  machine: CodedFields | undefined
): Set<OcrCodedField> {
  if (!machine) return new Set(OCR_CODED_FIELDS);

  const changed = new Set<OcrCodedField>();
  OCR_CODED_FIELDS.forEach((field) => {
    if (!sameValue(field, approved[field], machine[field])) changed.add(field);
  });
  return changed;
}

/**
 * Machine rows keyed for lookup, so a whole list can be diffed in one pass rather than scanning
 * the machine list once per approved row.
 */
export function codedByKey(rows: any[], toCoded: (row: any) => CodedFields): Map<string, CodedFields> {
  const byKey = new Map<string, CodedFields>();
  (rows ?? []).forEach((row) => {
    const coded = toCoded(row);
    const key =
      row?.stableKey ??
      stableKeyFor(Number(coded.distance ?? 0), String(coded.mainCode ?? ""), coded.timeSeconds);
    if (key) byKey.set(key, coded);
  });
  return byKey;
}

/** Look one approved row's machine counterpart up in a map built by {@link codedByKey}. */
export function machineCounterpart(
  approved: CodedFields,
  stableKey: string | undefined,
  byKey: Map<string, CodedFields>
): CodedFields | undefined {
  const key =
    stableKey ??
    stableKeyFor(
      Number(approved.distance ?? 0),
      String(approved.mainCode ?? ""),
      approved.timeSeconds
    );
  return byKey.get(key);
}
